import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Calendar, SlidersHorizontal, User, UserX, Phone, PenSquare, RefreshCw, Inbox, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ConvChatLabel } from '@/types/messaging';
import { useAppUsersStore } from '@/stores/app-users-store';
import { usePermissions } from '@/hooks/use-permissions';

interface ConversationSearchProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSearchByPhone: (phone: string) => void;
  phoneSearchActive: boolean;
  filterLabel: string | null;
  onFilterLabelChange: (label: string | null) => void;
  filterAgent: string;
  onFilterAgentChange: (agent: string) => void;
  chatLabels: Record<string, ConvChatLabel>;
  onNewChat?: () => void;
  onRefresh?: () => void;
  mentionCount?: number;
  mentionsPanel?: React.ReactNode;
  loading?: boolean;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  onDateFromChange?: (date: Date | null) => void;
  onDateToChange?: (date: Date | null) => void;
  unreadOnly?: boolean;
  onUnreadOnlyChange?: (unreadOnly: boolean) => void;
  /** Jump-to-date cutoff: show conversations last updated on or before this */
  jumpBefore?: Date | null;
  onJumpToDate?: (date: Date | null) => void;
  /** Hide assignee quick tabs (All/Mine/Unassigned) — e.g. in CRM record context */
  hideAssigneeFilters?: boolean;
  /** Hide label filter pills (Unread/Open/On Hold/Closed) — e.g. in CRM record context */
  hideLabelFilters?: boolean;
}

const DATE_PRESETS = [
  { label: 'Today', days: 0 },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

const QUICK_TABS: { key: string; label: string; icon: LucideIcon }[] = [
  { key: 'all', label: 'All', icon: Inbox },
  { key: 'mine', label: 'Mine', icon: User },
  { key: 'unassigned', label: 'Unassigned', icon: UserX },
];

export function ConversationSearch({
  searchQuery,
  onSearchChange,
  onSearchByPhone,
  phoneSearchActive,
  filterLabel,
  onFilterLabelChange,
  filterAgent,
  onFilterAgentChange,
  chatLabels,
  onNewChat,
  onRefresh,
  mentionCount: _mentionCount,
  mentionsPanel,
  loading,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  unreadOnly,
  onUnreadOnlyChange,
  jumpBefore,
  onJumpToDate,
  hideAssigneeFilters,
  hideLabelFilters,
}: ConversationSearchProps) {
  const { getLabelPermission, inboxPermissions, isAdmin } = usePermissions();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const labelsScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollIndicators = useCallback(() => {
    const el = labelsScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    updateScrollIndicators();
  }, [chatLabels, updateScrollIndicators]);

  useEffect(() => {
    const el = labelsScrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => updateScrollIndicators());
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollIndicators]);

  const orgUsers = useAppUsersStore((s) => s.orgUsers);
  const loadOrgUsers = useAppUsersStore((s) => s.loadOrgUsers);
  const orgUsersLoaded = useAppUsersStore((s) => s.loaded);

  // Load org users on mount for agent filter
  useEffect(() => {
    if (!orgUsersLoaded) {
      loadOrgUsers();
    }
  }, [orgUsersLoaded, loadOrgUsers]);

  /** Check if the search query looks like a phone number (mostly digits, at least 7) */
  const isPhoneNumber = useCallback((q: string) => {
    const digits = q.replace(/[\s\-()+.]/g, '');
    return /^\d{7,15}$/.test(digits);
  }, []);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      if (isPhoneNumber(searchQuery.trim())) {
        onSearchByPhone(searchQuery.trim());
      }
    }
  }, [searchQuery, isPhoneNumber, onSearchByPhone]);

  // "hasActiveFilters" only covers the advanced panel filters (agent-specific email, label, date, unread)
  const hasAdvancedFilters = filterLabel || (filterAgent && filterAgent !== 'all' && filterAgent !== 'mine' && filterAgent !== 'unassigned') || dateFrom || dateTo || unreadOnly || jumpBefore;

  const handleDatePreset = (days: number) => {
    if (!onDateFromChange || !onDateToChange) return;
    if (days === 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      onDateFromChange(today);
      onDateToChange(new Date());
    } else {
      const from = new Date();
      from.setDate(from.getDate() - days);
      from.setHours(0, 0, 0, 0);
      onDateFromChange(from);
      onDateToChange(new Date());
    }
  };

  // Sort labels by order_pos for display, filtering out no_access labels
  const sortedLabels = Object.entries(chatLabels)
    .filter(([key]) => getLabelPermission(key) !== 'no_access')
    .sort(([, a], [, b]) => (a.order_pos ?? 0) - (b.order_pos ?? 0));

  // Filter quick tabs based on inbox permissions (admins see all tabs).
  // When "canViewOtherUsersChats" is off → hide "All" tab.
  // When "canViewUnassignedChats" is off → hide "Unassigned" tab.
  // This forces the Firestore query to use assigneeEmail filter automatically.
  const visibleTabs = QUICK_TABS.filter((tab) => {
    if (isAdmin) return true;
    if (tab.key === 'all' && !inboxPermissions.canViewOtherUsersChats) return false;
    if (tab.key === 'unassigned' && !inboxPermissions.canViewUnassignedChats) return false;
    return true;
  });

  // filterAgent is either a quick-tab key ('all' / 'mine' / 'unassigned') or a
  // specific agent's email picked from the Agent pills below.
  const isQuickTabKey = QUICK_TABS.some((t) => t.key === filterAgent);
  const canFilterByAgent = isAdmin || inboxPermissions.canViewOtherUsersChats;

  // Auto-switch to "mine" when the current filter is not allowed for this user:
  // a quick tab that permissions hid, or a specific agent when the user may not
  // view other users' chats. A specific agent email is NOT a hidden tab — it
  // must be left alone, otherwise picking an agent pill snaps back to "Mine".
  const isCurrentTabHidden = isQuickTabKey
    ? !visibleTabs.some((t) => t.key === filterAgent)
    : !canFilterByAgent;
  useEffect(() => {
    if (isCurrentTabHidden) {
      onFilterAgentChange('mine');
    }
  }, [isCurrentTabHidden, onFilterAgentChange]);

  // Determine active quick tab (none when a specific agent is selected)
  const activeTab = isQuickTabKey
    ? (visibleTabs.find((t) => t.key === filterAgent)?.key || 'mine')
    : null;

  const clearFilters = () => {
    onFilterLabelChange(null);
    // Reset to the first visible tab (respects inbox permissions)
    onFilterAgentChange(visibleTabs[0]?.key || 'mine');
    onDateFromChange?.(null);
    onDateToChange?.(null);
    onUnreadOnlyChange?.(false);
    onJumpToDate?.(null);
  };

  const handleTabChange = (key: string) => {
    onFilterAgentChange(key);
  };

  return (
    <div className="border-b border-border bg-background dark:bg-card">
      {/* Search input + action icons */}
      <div className="flex items-center gap-2 px-3 pt-2.5 pb-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            data-testid="search-conversations"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search conversations..."
            className="w-full rounded-full border border-input bg-background pl-8 pr-9 py-1.5 text-[13px] text-foreground placeholder:text-muted-foreground shadow-[inset_0_1px_1px_rgba(0,0,0,0.04)] dark:shadow-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-colors"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
            {searchQuery && (
              <button
                onClick={() => onSearchChange('')}
                className="rounded-full p-0.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              data-testid="advanced-filters-btn"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={cn(
                'rounded-full p-1 transition-colors',
                showAdvanced || hasAdvancedFilters
                  ? 'text-primary bg-primary/10'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              title="Advanced filters"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {onNewChat && (
          <button
            data-testid="new-conversation-btn"
            onClick={onNewChat}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            title="New conversation"
          >
            <PenSquare className="h-3.5 w-3.5" />
            New
          </button>
        )}
        {mentionsPanel !== undefined && (
          <div className="relative shrink-0">
            {mentionsPanel}
          </div>
        )}
        {onRefresh && (
          <button
            data-testid="refresh-btn"
            onClick={onRefresh}
            disabled={loading}
            className="shrink-0 rounded-lg p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </button>
        )}
      </div>

      {/* Phone search indicator */}
      {phoneSearchActive && (
        <div className="flex items-center gap-2 px-3 pb-2">
          <div className="flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium">
            <Phone className="h-3 w-3" />
            Showing results for {searchQuery}
          </div>
          <button
            onClick={() => onSearchChange('')}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      {/* Quick filter tabs — hidden in CRM record context or when only one tab */}
      {!hideAssigneeFilters && visibleTabs.length > 1 && (
        <div className="px-3 py-2 border-t border-border/50">
          <div className="flex items-center rounded-full border border-border bg-muted/50 dark:bg-background p-0.5">
            {visibleTabs.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                data-testid={`tab-${key}`}
                onClick={() => handleTabChange(key)}
                className={cn(
                  'flex-1 inline-flex items-center justify-center gap-1 rounded-full py-1.5 text-xs font-medium transition-colors',
                  activeTab === key
                    ? 'text-primary bg-background border border-primary/20'
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/70 dark:hover:bg-muted/50'
                )}
              >
                <Icon className="h-3 w-3" />
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Label filters — hidden in CRM record context */}
      {!hideLabelFilters && <div className="relative px-3 py-1.5 border-t border-border/50">
        {canScrollLeft && (
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        )}
        {canScrollRight && (
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
        )}
        <div
          ref={labelsScrollRef}
          onScroll={updateScrollIndicators}
          className="flex items-center gap-1.5 overflow-x-auto scrollbar-none"
        >
          <button
            data-testid="status-all"
            onClick={() => onFilterLabelChange(null)}
            className={cn(
              'shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors',
              !filterLabel
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            )}
          >
            All
          </button>
          {getLabelPermission('__unread__') !== 'no_access' && (
            <button
              data-testid="status-unread"
              onClick={() => onFilterLabelChange(filterLabel === '__unread__' ? null : '__unread__')}
              className={cn(
                'shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors inline-flex items-center gap-1',
                filterLabel === '__unread__'
                  ? 'bg-blue-600 text-white'
                  : 'text-blue-600 bg-blue-600/10 hover:opacity-80'
              )}
            >
              <span
                className={cn(
                  'rounded-full shrink-0 w-1.5 h-1.5',
                  filterLabel === '__unread__' ? 'bg-white' : 'bg-blue-600'
                )}
              />
              Unread
            </button>
          )}
          {getLabelPermission('__send_failed__') !== 'no_access' && (
            <button
              data-testid="status-sendfailed"
              onClick={() => onFilterLabelChange(filterLabel === '__send_failed__' ? null : '__send_failed__')}
              className={cn(
                'shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors inline-flex items-center gap-1',
                filterLabel === '__send_failed__'
                  ? 'bg-red-600 text-white'
                  : 'text-red-600 bg-red-600/10 hover:opacity-80'
              )}
            >
              <span
                className={cn(
                  'rounded-full shrink-0 w-1.5 h-1.5',
                  filterLabel === '__send_failed__' ? 'bg-white' : 'bg-red-600'
                )}
              />
              Send Failed
            </button>
          )}
          {sortedLabels.map(([key, label]) => {
            const isActive = filterLabel === key;
            return (
              <LabelPill
                key={key}
                data-testid={`status-${label.display_label.toLowerCase().replace(/\s+/g, '')}`}
                label={label.display_label}
                color={label.color}
                isActive={isActive}
                onClick={() => onFilterLabelChange(isActive ? null : key)}
              />
            );
          })}
        </div>
      </div>}

      {/* Advanced filters — shown when filter icon is clicked */}
      {showAdvanced && (
        <div className="space-y-2 px-3 py-2 border-t border-border/50 bg-muted/30">
          {/* Agent team member pills — only for users allowed to view other
              agents' chats; otherwise a click would be committed, fetched, and
              then undone by the reset effect above (double reload). */}
          {canFilterByAgent && Object.keys(orgUsers).length > 0 && (
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none">
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider shrink-0">Agent</span>
              {Object.values(orgUsers).map((user) => {
                const isActive = filterAgent === user.email;
                return (
                  <button
                    key={user.email}
                    onClick={() => onFilterAgentChange(isActive ? (visibleTabs[0]?.key || 'mine') : user.email)}
                    className={cn(
                      'shrink-0 rounded-md px-2 py-1 text-xs font-medium transition-colors inline-flex items-center gap-1',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                  >
                    <User className="h-3 w-3" />
                    {user.name || user.email.split('@')[0]}
                  </button>
                );
              })}
            </div>
          )}

          {/* Advanced filters panel — date range, unread toggle, jump to date */}
          {(onDateFromChange || onUnreadOnlyChange || onJumpToDate || hasAdvancedFilters) && (
            <div className="space-y-3 pt-2 border-t border-border/50">
              {/* Jump to conversations last updated on/before a given date */}
              {onJumpToDate && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">Jump to date</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      max={new Date().toISOString().split('T')[0]}
                      value={jumpBefore ? jumpBefore.toISOString().split('T')[0] : ''}
                      onChange={(e) => {
                        if (!e.target.value) {
                          onJumpToDate(null);
                          return;
                        }
                        const [y, m, d] = e.target.value.split('-').map(Number);
                        onJumpToDate(new Date(y, m - 1, d));
                      }}
                      className="flex-1 rounded-lg border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                    {jumpBefore && (
                      <button
                        onClick={() => onJumpToDate(null)}
                        className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              )}
              {/* Date range */}
              {onDateFromChange && onDateToChange && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">Date Range</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {DATE_PRESETS.map((preset) => (
                      <button
                        key={preset.label}
                        onClick={() => handleDatePreset(preset.days)}
                        className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={dateFrom ? dateFrom.toISOString().split('T')[0] : ''}
                      onChange={(e) =>
                        onDateFromChange(e.target.value ? new Date(e.target.value) : null)
                      }
                      className="flex-1 rounded-lg border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                    <span className="text-xs text-muted-foreground self-center">to</span>
                    <input
                      type="date"
                      value={dateTo ? dateTo.toISOString().split('T')[0] : ''}
                      onChange={(e) =>
                        onDateToChange(e.target.value ? new Date(e.target.value) : null)
                      }
                      className="flex-1 rounded-lg border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                </div>
              )}

              {/* Unread only toggle */}
              {onUnreadOnlyChange && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="unread-only-toggle"
                    type="checkbox"
                    checked={unreadOnly || false}
                    onChange={(e) => onUnreadOnlyChange(e.target.checked)}
                    className="rounded border-input"
                  />
                  <span className="text-xs text-muted-foreground">Unread only</span>
                </label>
              )}

              {/* Clear all filters */}
              {hasAdvancedFilters && (
                <button
                  data-testid="clear-filters-btn"
                  onClick={clearFilters}
                  className="text-xs text-destructive hover:underline"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Helper: parse any CSS color to rgba with alpha. Uses a canvas for reliable parsing. */
function colorWithAlpha(color: string, alpha: number): string {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return color;
    ctx.fillStyle = color;
    // ctx.fillStyle normalizes any valid CSS color to #rrggbb or rgba()
    const normalized = ctx.fillStyle;
    if (normalized.startsWith('#')) {
      const hex = normalized.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r},${g},${b},${alpha})`;
    }
    return normalized.replace(')', `,${alpha})`).replace('rgb(', 'rgba(');
  } catch {
    return color;
  }
}

function LabelPill({ label, color, isActive, onClick, 'data-testid': testId }: { label: string; color: string; isActive: boolean; onClick: () => void; 'data-testid'?: string }) {
  const bg = isActive ? color : colorWithAlpha(color, 0.1);
  const fg = isActive ? '#ffffff' : color;
  const dotColor = isActive ? '#ffffff' : color;

  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={cn(
        'shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors inline-flex items-center gap-1',
        !isActive && 'hover:opacity-80'
      )}
      style={{ backgroundColor: bg, color: fg }}
    >
      <span
        className="rounded-full shrink-0"
        style={{ width: 6, height: 6, backgroundColor: dotColor }}
      />
      {label}
    </button>
  );
}
